import AuthenticationType from '@alltrails/analytics/enums/AuthenticationType';
import { GoogleLoginResponse } from 'react-google-login';
import { ApiError, post } from '@alltrails/shared/api';
import LoginResponse from 'types/LoginResponse';
import logLogInFailed from '@alltrails/analytics/events/logLogInFailed';
import logSignUpFailed from '@alltrails/analytics/events/logSignUpFailed';
import { LegacyAPIError } from 'types/Errors';
import User from '@alltrails/shared/types/User';
import { ServerCommunicationUtil } from './server_communication_util';
import { authSuccessCallback } from './AuthHandlers';
import { appleConstants } from '../Constants';

// Note: These handlers are used for both new registrations (signups) AND logins

const facebookHandler = (
  response: { accessToken: any; userID: any; expiresIn: any },
  locale: string,
  successCallback: Function = () => {},
  errorCallback = (err: LegacyAPIError) => {},
  completeCallback = () => {},
  handlerType: 'login' | 'signup',
  referralLink?: string
) => {
  if (!response.accessToken) {
    return;
  }

  const fbData = {
    facebook_id: response.userID,
    facebook_token: response.accessToken,
    facebook_expires: response.expiresIn,
    create_session: true,
    ...(referralLink && { referral_code: referralLink })
  };
  const headers = locale ? { 'X-Language-Locale': locale } : {};

  ServerCommunicationUtil.postApiEndpoint(
    '/api/alltrails/facebook/login',
    fbData,
    (data: any) => {
      authSuccessCallback(data, AuthenticationType.Facebook);
      successCallback(data);
    },
    (err: LegacyAPIError) => {
      const errorMessage = err?.responseJSON?.errors?.[0]?.code || 'unknown';
      if (handlerType === 'signup') {
        logSignUpFailed({ authentication_type: AuthenticationType.Facebook, failure_type: errorMessage });
      } else {
        logLogInFailed({ authentication_type: AuthenticationType.Facebook, failure_type: errorMessage });
      }
      errorCallback(err);
    },
    completeCallback,
    headers
  );
};

// Note: This handler works differently than the others, where the user is redirected to
// an Apple login page and then redirected back to AllTrails.
// Because of this, there is a tracking callback implemented in GlobalActions
// that tracks that the signup/login redirect was successful.
const appleHandler = (referralLink?: string) => {
  let redirectURI = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}${
    appleConstants.REDIRECT_PATH
  }`;

  if (referralLink) {
    const params = new URLSearchParams();
    params.set('referral_code', referralLink);
    redirectURI += `?${params.toString()}`;
  }

  const authUrl = `${appleConstants.AUTHORIZE_URL + appleConstants.AUTHORIZE_PARAMS}&client_id=${
    appleConstants.CLIENT_ID
  }&redirect_uri=${encodeURIComponent(redirectURI)}&scope=${encodeURIComponent(appleConstants.SCOPES)}`;

  window.location.href = authUrl;
};

const appleRedirectHandler = (user: User) => {
  const params = new URLSearchParams(window.location.search);
  const appleOAuth = params.get('apple_oauth');
  if (appleOAuth) {
    const is_sign_up = appleOAuth === 'signup';
    authSuccessCallback({ is_sign_up, user }, AuthenticationType.Apple);
    const search = params.toString();
    window.history.replaceState(null, null, window.location.pathname + (search !== '' ? `?${search}` : ''));
  }
};

async function googleHandler(
  googleUserOrToken: GoogleLoginResponse | string,
  handlerType: 'login' | 'signup',
  referralLink?: string,
  oneTap?: boolean
) {
  const token = typeof googleUserOrToken === 'string' ? googleUserOrToken : googleUserOrToken.getAuthResponse().id_token;

  const data = {
    google_token: token,
    create_session: true,
    ...(referralLink && { referral_code: referralLink }),
    ...(oneTap && { one_tap: oneTap })
  };

  let response;

  try {
    response = await post<LoginResponse>('/api/alltrails/google/login', data);
    authSuccessCallback(response, oneTap ? AuthenticationType.GoogleOneTap : AuthenticationType.Google);
  } catch (error: unknown) {
    const errorMessage = error instanceof ApiError && error?.statusText ? error.statusText : 'unknown';
    if (handlerType === 'signup') {
      logSignUpFailed({ authentication_type: oneTap ? AuthenticationType.GoogleOneTap : AuthenticationType.Google, failure_type: errorMessage });
    } else {
      logLogInFailed({ authentication_type: oneTap ? AuthenticationType.GoogleOneTap : AuthenticationType.Google, failure_type: errorMessage });
    }
  }

  return response;
}

export { facebookHandler, appleHandler, appleRedirectHandler, googleHandler };
